import React, { useCallback, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { type Role, surveyData } from './data/surveyData';
import { RoleSelector } from './components/RoleSelector';
import { RoleToggle } from './components/RoleToggle';
import { HookSlide } from './components/HookSlide';
import { QuestionSlide } from './components/QuestionSlide';
import { FeedbackSlide } from './components/FeedbackSlide';
import { ClosingSlide } from './components/ClosingSlide';
import { IntakeForm } from './components/IntakeForm';
type SlideType = 'role-select' | 'hook' | 'question' | 'feedback' | 'closing' | 'intake';
interface SlideState {
  type: SlideType;
  questionIndex?: number;
}
const slideTransition = {
  initial: {
    opacity: 0,
    y: 20
  },
  animate: {
    opacity: 1,
    y: 0
  },
  exit: {
    opacity: 0,
    y: -20
  },
  transition: {
    duration: 0.3,
    ease: 'easeInOut'
  }
};
export function App() {
  const [selectedRole, setSelectedRole] = useState<Role>('Nephrologist');
  const [viewingRole, setViewingRole] = useState<Role>('Nephrologist');
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [slide, setSlide] = useState<SlideState>({
    type: 'role-select'
  });
  const handleRoleSelect = useCallback((role: Role) => {
    setSelectedRole(role);
    setViewingRole(role);
    setSlide({
      type: 'hook'
    });
  }, []);
  const handleStart = useCallback(() => {
    setSlide({
      type: 'question',
      questionIndex: 0
    });
  }, []);
  const handleAnswer = useCallback((questionIndex: number, optionId: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: optionId
    }));
    setSlide({
      type: 'feedback',
      questionIndex
    });
  }, []);
  const handleContinue = useCallback((questionIndex: number) => {
    if (questionIndex < surveyData.length - 1) {
      setSlide({
        type: 'question',
        questionIndex: questionIndex + 1
      });
    } else {
      setSlide({
        type: 'closing'
      });
    }
  }, []);
  const handleSkip = useCallback((questionIndex: number) => {
    if (questionIndex < surveyData.length - 1) {
      setSlide({
        type: 'question',
        questionIndex: questionIndex + 1
      });
    } else {
      setSlide({
        type: 'closing'
      });
    }
  }, []);
  const handleQuestionBack = useCallback((questionIndex: number) => {
    if (questionIndex === 0) {
      setSlide({
        type: 'hook'
      });
    } else {
      // Go back to previous feedback if answered, otherwise previous question
      const prevIndex = questionIndex - 1;
      if (answers[prevIndex] !== undefined) {
        setSlide({
          type: 'feedback',
          questionIndex: prevIndex
        });
      } else {
        setSlide({
          type: 'question',
          questionIndex: prevIndex
        });
      }
    }
  }, [answers]);
  const handleFeedbackBack = useCallback((questionIndex: number) => {
    setSlide({
      type: 'question',
      questionIndex
    });
  }, []);
  const handleClosingBack = useCallback(() => {
    const lastIndex = surveyData.length - 1;
    if (answers[lastIndex] !== undefined) {
      setSlide({
        type: 'feedback',
        questionIndex: lastIndex
      });
    } else {
      setSlide({
        type: 'question',
        questionIndex: lastIndex
      });
    }
  }, [answers]);
  const handleRequestInfo = useCallback(() => {
    setSlide({
      type: 'intake'
    });
  }, []);
  const handleIntakeBack = useCallback(() => {
    setSlide({
      type: 'closing'
    });
  }, []);
  const handleRestart = useCallback(() => {
    setAnswers({});
    setSlide({
      type: 'role-select'
    });
  }, []);
  const showTopBar = slide.type !== 'role-select' && slide.type !== 'feedback' && slide.type !== 'intake';
  const showLogoBar = slide.type === 'feedback' || slide.type === 'intake';
  const slideKey = `${slide.type}-${slide.questionIndex ?? ''}`;
  return <div className="min-h-screen w-full max-w-md mx-auto bg-white flex flex-col relative" data-id="element-0">
      {/* ERA top color bar */}
      <div className="h-1 w-full bg-gradient-to-r from-era-teal via-era-blue to-era-red flex-shrink-0" data-id="element-1" />

      {showTopBar && <motion.div initial={{
      opacity: 0,
      y: -8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.25
    }} data-id="element-2">
          <RoleToggle currentRole={viewingRole} onRoleChange={setViewingRole} data-id="element-3" />
        </motion.div>}

      {showLogoBar && <div className="w-full bg-white border-b border-gray-100 px-4 py-2.5 flex items-center justify-center gap-3" data-id="element-4">
          <img src="https://cdn.magicpatterns.com/uploads/ij2eNBe1HzJZKgaPMQ9K3H/Screenshot_2026-03-31_at_15.45.48.png" alt="ERA" className="h-6 object-contain" data-id="element-5" />
          <div className="w-px h-5 bg-gray-200" data-id="element-6" />
          <img src="https://cdn.magicpatterns.com/uploads/ezbVhRkDuKPWYLVJz9KwsF/Screenshot_2026-03-31_at_16.01.46.png" alt="CSL" className="h-5 object-contain" data-id="element-7" />
        </div>}

      <AnimatePresence mode="wait" data-id="element-8">
        <motion.div key={slideKey} className="flex-1 flex flex-col" {...slideTransition} data-id="element-9">
          {slide.type === 'role-select' && <RoleSelector onSelect={handleRoleSelect} data-id="element-10" />}

          {slide.type === 'hook' && <HookSlide onStart={handleStart} data-id="element-11" />}

          {slide.type === 'question' && slide.questionIndex !== undefined && <QuestionSlide questionNumber={slide.questionIndex + 1} totalQuestions={surveyData.length} question={surveyData[slide.questionIndex].question} options={surveyData[slide.questionIndex].options} onAnswer={optionId => handleAnswer(slide.questionIndex!, optionId)} onBack={() => handleQuestionBack(slide.questionIndex!)} onSkip={() => handleSkip(slide.questionIndex!)} canGoBack={true} data-id="element-12" />}

          {slide.type === 'feedback' && slide.questionIndex !== undefined && <FeedbackSlide questionData={surveyData[slide.questionIndex]} currentRole={viewingRole} userAnswerId={answers[slide.questionIndex] || ''} onContinue={() => handleContinue(slide.questionIndex!)} onBack={() => handleFeedbackBack(slide.questionIndex!)} onRoleChange={setViewingRole} isLast={slide.questionIndex === surveyData.length - 1} data-id="element-13" />}

          {slide.type === 'closing' && <ClosingSlide onRestart={handleRestart} onRequestInfo={handleRequestInfo} onBack={handleClosingBack} data-id="element-14" />}

          {slide.type === 'intake' && <IntakeForm selectedRole={selectedRole} onBack={handleIntakeBack} data-id="element-15" />}
        </motion.div>
      </AnimatePresence>
    </div>;
}